/**
 * useVoiceCapture — wraps the browser's native SpeechRecognition.
 *
 * Phase 2A uses the free, on-device(ish) Web Speech API. Phase 2B will
 * swap in Deepgram streaming via WebSocket when DEEPGRAM_API_KEY is set
 * on the server — same callback contract, so consumers don't change.
 *
 * Browser support:
 *  - Chrome / Edge / Opera   ✅ (uses Google's cloud STT under the hood)
 *  - Safari iOS 14.5+        ✅
 *  - Safari macOS 14.1+      ✅
 *  - Firefox                 ❌ (no SpeechRecognition prefix yet)
 */
import { useCallback, useEffect, useRef, useState } from "react";

// Browser API types (not in TS lib by default for the webkit prefix)
interface SpeechRecognitionEventLike {
	resultIndex: number;
	results: ArrayLike<{
		isFinal: boolean;
		0: { transcript: string };
	}>;
}

interface SpeechRecognitionErrorEventLike {
	error: string;
}

interface SpeechRecognitionLike {
	lang: string;
	continuous: boolean;
	interimResults: boolean;
	start: () => void;
	stop: () => void;
	abort: () => void;
	onresult: ((e: SpeechRecognitionEventLike) => void) | null;
	onend: (() => void) | null;
	onerror: ((e: SpeechRecognitionErrorEventLike) => void) | null;
	onstart: (() => void) | null;
}

type SpeechRecognitionCtor = new () => SpeechRecognitionLike;

function getRecognitionCtor(): SpeechRecognitionCtor | null {
	if (typeof window === "undefined") return null;
	const w = window as unknown as {
		SpeechRecognition?: SpeechRecognitionCtor;
		webkitSpeechRecognition?: SpeechRecognitionCtor;
	};
	return (w.SpeechRecognition || w.webkitSpeechRecognition) ?? null;
}

export type VoiceCaptureState = "idle" | "listening" | "error";

interface UseVoiceCaptureOptions {
	/** BCP-47 tag — 'ar-IQ', 'ar-SA', 'en-US', etc. */
	lang: string;
	/** Called with the final transcript when the user stops speaking. */
	onFinal?: (text: string) => void;
}

export function useVoiceCapture({ lang, onFinal }: UseVoiceCaptureOptions) {
	const recRef = useRef<SpeechRecognitionLike | null>(null);
	const finalCallbackRef = useRef(onFinal);
	finalCallbackRef.current = onFinal;

	const [state, setState] = useState<VoiceCaptureState>("idle");
	const [interim, setInterim] = useState("");
	const [finalText, setFinalText] = useState("");
	const [error, setError] = useState<string | null>(null);

	const supported = getRecognitionCtor() !== null;

	const stop = useCallback(() => {
		recRef.current?.stop();
	}, []);

	const start = useCallback(() => {
		const Ctor = getRecognitionCtor();
		if (!Ctor) {
			setError("not-supported");
			setState("error");
			return;
		}

		// Tear down any previous instance
		recRef.current?.abort();

		const rec = new Ctor();
		rec.lang = lang;
		rec.continuous = false;
		rec.interimResults = true;

		let accumulatedFinal = "";

		rec.onstart = () => {
			setError(null);
			setInterim("");
			setFinalText("");
			setState("listening");
		};

		rec.onresult = (e) => {
			let interimChunk = "";
			for (let i = e.resultIndex; i < e.results.length; i++) {
				const r = e.results[i];
				if (!r) continue;
				const segment = r[0]?.transcript ?? "";
				if (r.isFinal) {
					accumulatedFinal += segment;
				} else {
					interimChunk += segment;
				}
			}
			setInterim(interimChunk);
			if (accumulatedFinal) setFinalText(accumulatedFinal);
		};

		rec.onerror = (e) => {
			// "no-speech" / "aborted" are routine — don't treat as fatal
			if (e.error === "no-speech" || e.error === "aborted") {
				setState("idle");
				return;
			}
			setError(e.error);
			setState("error");
		};

		rec.onend = () => {
			setState("idle");
			setInterim("");
			const trimmed = accumulatedFinal.trim();
			if (trimmed && finalCallbackRef.current) {
				finalCallbackRef.current(trimmed);
			}
		};

		recRef.current = rec;
		try {
			rec.start();
		} catch (err) {
			setError((err as Error).message);
			setState("error");
		}
	}, [lang]);

	useEffect(() => () => recRef.current?.abort(), []);

	return {
		state,
		supported,
		interim,
		finalText,
		error,
		start,
		stop,
	};
}
